<template>
  <div class="app-bar-clock" role="timer" aria-live="off" :aria-label="ariaLabel">
    <div class="app-bar-clock__glow" aria-hidden="true" />
    <div class="app-bar-clock__sheen" aria-hidden="true" />

    <div class="app-bar-clock__date-row">
      <span class="app-bar-clock__day">{{ parts.dayName }}</span>
      <span class="app-bar-clock__date-sep" aria-hidden="true">·</span>
      <span class="app-bar-clock__date">{{ parts.dateLabel }}</span>
    </div>

    <div class="app-bar-clock__time-row" aria-hidden="true">
      <div class="app-bar-clock__unit">
        <Transition name="app-bar-clock-tick" mode="out-in">
          <span :key="parts.hours" class="app-bar-clock__digits">{{ parts.hours }}</span>
        </Transition>
        <span class="app-bar-clock__unit-label">Hr</span>
      </div>

      <span class="app-bar-clock__colon app-bar-clock__colon--pulse">:</span>

      <div class="app-bar-clock__unit">
        <Transition name="app-bar-clock-tick" mode="out-in">
          <span :key="parts.minutes" class="app-bar-clock__digits">{{ parts.minutes }}</span>
        </Transition>
        <span class="app-bar-clock__unit-label">Min</span>
      </div>

      <span class="app-bar-clock__colon app-bar-clock__colon--pulse">:</span>

      <div class="app-bar-clock__unit app-bar-clock__unit--seconds">
        <Transition name="app-bar-clock-tick" mode="out-in">
          <span :key="parts.seconds" class="app-bar-clock__digits">{{ parts.seconds }}</span>
        </Transition>
        <span class="app-bar-clock__unit-label">Sec</span>
      </div>

      <Transition name="app-bar-clock-ampm" mode="out-in">
        <span
          :key="parts.ampm"
          class="app-bar-clock__ampm"
          :class="parts.ampm === 'AM' ? 'app-bar-clock__ampm--am' : 'app-bar-clock__ampm--pm'"
        >{{ parts.ampm }}</span>
      </Transition>
    </div>
  </div>
</template>

<script>
export default {
  name: "AppBarClock",

  data() {
    return {
      now: new Date(),
      timerId: null,
    };
  },

  computed: {
    parts() {
      const d = this.now;
      let hours = d.getHours();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      if (hours === 0) hours = 12;

      return {
        hours: String(hours).padStart(2, "0"),
        minutes: String(d.getMinutes()).padStart(2, "0"),
        seconds: String(d.getSeconds()).padStart(2, "0"),
        ampm,
        dayName: d.toLocaleDateString(undefined, { weekday: "long" }),
        dateLabel: d.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      };
    },

    ariaLabel() {
      const p = this.parts;
      return `${p.dayName}, ${p.dateLabel}, ${p.hours}:${p.minutes}:${p.seconds} ${p.ampm}`;
    },
  },

  mounted() {
    this.now = new Date();
    this.timerId = window.setInterval(() => {
      this.now = new Date();
    }, 1000);
  },

  beforeUnmount() {
    if (this.timerId != null) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
  },
};
</script>

<style scoped>
.app-bar-clock {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 6px 12px 7px;
  border-radius: 12px;
  overflow: hidden;
  background: var(--sw-shell-glass-bg);
  border: 1px solid var(--sw-shell-glass-border);
  box-shadow:
    0 4px 14px rgba(var(--sw-primary-rgb), 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(12px) saturate(1.35);
  -webkit-backdrop-filter: blur(12px) saturate(1.35);
}

.app-bar-clock__sheen,
.app-bar-clock__glow {
  position: absolute;
  pointer-events: none;
  z-index: 0;
}

.app-bar-clock__sheen {
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    145deg,
    rgba(123, 4, 108, 0.09) 0%,
    rgba(155, 45, 173, 0.04) 42%,
    transparent 72%
  );
}

.app-bar-clock__glow {
  top: -24px;
  left: 50%;
  translate: -50% 0;
  width: 100px;
  height: 72px;
  border-radius: 50%;
  background: var(--sw-shell-glass-glow);
}

.app-bar-clock__date-row,
.app-bar-clock__time-row {
  position: relative;
  z-index: 1;
}

.app-bar-clock__date-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 100%;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--sw-muted);
  line-height: 1.2;
  white-space: nowrap;
}

.app-bar-clock__day {
  color: var(--sw-primary);
  text-transform: capitalize;
}

.app-bar-clock__date-sep {
  opacity: 0.45;
}

.app-bar-clock__date {
  color: var(--sw-text-secondary);
}

.app-bar-clock__time-row {
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
}

.app-bar-clock__unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 28px;
}

.app-bar-clock__unit--seconds .app-bar-clock__digits {
  color: var(--sw-primary);
}

.app-bar-clock__digits {
  display: block;
  font-family: var(
    --app-font-mono,
    ui-monospace,
    "SF Mono",
    "Cascadia Code",
    monospace
  );
  font-size: 1.02rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: 0.04em;
  color: var(--sw-text-strong);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
}

html[data-theme="dark"] .app-bar-clock__digits {
  text-shadow: none;
}

.app-bar-clock__unit-label {
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--sw-section-label);
  line-height: 1;
}

.app-bar-clock__colon {
  align-self: flex-start;
  margin-top: 2px;
  font-family: var(
    --app-font-mono,
    ui-monospace,
    "SF Mono",
    "Cascadia Code",
    monospace
  );
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  color: rgba(var(--sw-primary-rgb), 0.45);
}

.app-bar-clock__colon--pulse {
  animation: app-bar-clock-colon-pulse 1s ease-in-out infinite;
}

.app-bar-clock__ampm {
  align-self: flex-start;
  margin: 1px 0 0 6px;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  line-height: 1.35;
  border: 1.5px solid transparent;
  box-shadow: none;
}

.app-bar-clock__ampm--am {
  color: #15803d;
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(34, 197, 94, 0.42);
}

.app-bar-clock__ampm--pm {
  color: #1d4ed8;
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.4);
}

html[data-theme="dark"] .app-bar-clock__ampm--am {
  color: #4ade80;
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(74, 222, 128, 0.38);
}

html[data-theme="dark"] .app-bar-clock__ampm--pm {
  color: #60a5fa;
  background: rgba(59, 130, 246, 0.14);
  border-color: rgba(96, 165, 250, 0.4);
}

/* Tick transitions */
.app-bar-clock-tick-enter-active,
.app-bar-clock-tick-leave-active {
  transition:
    opacity 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.app-bar-clock-tick-enter-from {
  opacity: 0;
  transform: translateY(7px) scale(0.92);
}

.app-bar-clock-tick-leave-to {
  opacity: 0;
  transform: translateY(-7px) scale(0.92);
}

.app-bar-clock-ampm-enter-active,
.app-bar-clock-ampm-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.app-bar-clock-ampm-enter-from,
.app-bar-clock-ampm-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

@keyframes app-bar-clock-colon-pulse {
  0%,
  100% {
    opacity: 0.35;
  }

  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-bar-clock__colon--pulse {
    animation: none;
    opacity: 0.7;
  }

  .app-bar-clock-tick-enter-active,
  .app-bar-clock-tick-leave-active,
  .app-bar-clock-ampm-enter-active,
  .app-bar-clock-ampm-leave-active {
    transition-duration: 0.01ms !important;
  }
}

@media (max-width: 900px) {
  .app-bar-clock__unit-label {
    display: none;
  }

  .app-bar-clock__date-row {
    font-size: 0.64rem;
  }
}

@media (max-width: 768px) {
  .app-bar-clock {
    display: none;
  }
}
</style>
